import { useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Sparkles, ArrowUpRight } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SEASONS = ["Peak", "Shoulder", "Off-peak"];
const TYPES = ["Deluxe Double", "Superior King", "Standard", "Studio Suite", "Presidential Suite"];

export default function RevenueAI() {
  const [form, setForm] = useState({
    room_type: "Deluxe Double",
    base_rate: 5200,
    occupancy_pct: 75,
    day_of_week: "Saturday",
    season: "Peak",
    competitor_avg_rate: 5400,
    special_event: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function suggest() {
    setLoading(true); setResult(null);
    try {
      const payload = { ...form, base_rate: Number(form.base_rate), occupancy_pct: Number(form.occupancy_pct), competitor_avg_rate: Number(form.competitor_avg_rate) };
      const { data } = await api.post("/revenue/suggest", payload);
      setResult(data);
      toast.success("Cerebrum suggested a rate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI request failed");
    } finally { setLoading(false); }
  }

  return (
    <div className="space-y-8" data-testid="revenue-ai-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Cerebrum</div>
        <h1 className="font-display text-3xl md:text-4xl">AI Revenue Manager</h1>
        <p className="text-white/60 mt-3 max-w-2xl">Get an optimal nightly rate recommendation powered by Claude Sonnet 4.5. Adjust context below and let Cerebrum reason.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#12182B] border border-white/5 rounded-xl p-6 space-y-5" data-testid="revenue-form">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Room type">
              <select value={form.room_type} onChange={(e) => upd("room_type", e.target.value)} className="input" data-testid="revenue-room-type">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Base rate (₹)">
              <input type="number" value={form.base_rate} onChange={(e) => upd("base_rate", e.target.value)} className="input" data-testid="revenue-base-rate" />
            </Field>
            <Field label="Occupancy %">
              <input type="number" min={0} max={100} value={form.occupancy_pct} onChange={(e) => upd("occupancy_pct", e.target.value)} className="input" data-testid="revenue-occupancy" />
            </Field>
            <Field label="Competitor avg (₹)">
              <input type="number" value={form.competitor_avg_rate} onChange={(e) => upd("competitor_avg_rate", e.target.value)} className="input" data-testid="revenue-competitor" />
            </Field>
            <Field label="Day of week">
              <select value={form.day_of_week} onChange={(e) => upd("day_of_week", e.target.value)} className="input" data-testid="revenue-day">
                {DAYS.map((d) => <option key={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Season">
              <select value={form.season} onChange={(e) => upd("season", e.target.value)} className="input" data-testid="revenue-season">
                {SEASONS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Special event (optional)">
            <input type="text" placeholder="e.g. Diwali weekend, IPL final" value={form.special_event} onChange={(e) => upd("special_event", e.target.value)} className="input" data-testid="revenue-event" />
          </Field>
          <button onClick={suggest} disabled={loading} className="btn-gold rounded-md px-5 py-3 inline-flex items-center gap-2 disabled:opacity-60" data-testid="revenue-suggest-button">
            <Sparkles className="w-4 h-4" />
            {loading ? "Analysing…" : "Suggest optimal rate"}
          </button>
        </div>

        <div className="bg-[#12182B] border border-white/5 rounded-xl p-6 relative overflow-hidden" data-testid="revenue-result">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Recommendation</div>
            {!result && !loading && <div className="text-white/50 text-sm">Fill the context on the left, then ask Cerebrum for an optimal rate.</div>}
            {loading && <div className="text-white/70 animate-pulse">Cerebrum is thinking…</div>}
            {result && (
              <div>
                <div className="flex items-end gap-3">
                  <div className="font-display text-6xl gold-shine">₹{Number(result.suggested_rate).toLocaleString("en-IN")}</div>
                  {result.delta_pct !== undefined && (
                    <div className={`text-sm inline-flex items-center gap-1 mb-2 ${Number(result.delta_pct) >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
                      <ArrowUpRight className="w-4 h-4" /> {result.delta_pct}% vs base
                    </div>
                  )}
                </div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-3">Confidence: <span className="text-[#D4AF37]">{result.confidence}</span></div>
                <div className="mt-6 text-white/75 leading-relaxed text-sm border-t border-white/5 pt-5">{result.reasoning}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .input { width: 100%; background: #0a0f1c; border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 10px 12px; color: #F8F9FA; font-size: 14px; outline: none; transition: border-color .2s; }
        .input:focus { border-color: rgba(212, 175, 55, 0.6); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
      {children}
    </div>
  );
}
